import qs from "qs"
import { FilterTypesInstantSearch } from "~/utils/enums/FilterTypesInstantSearch"
import { FilterTypesContentService } from "~/utils/enums/FilterTypesContentService"
import { normalizeMalformedQuery } from "~/utils/normalizeMalformedQuery"
import { sanitizeAndAddIfNotEmpty, sanitizeForeignQueries } from "~/utils/sanitizeRoute"

const indexName = "offers"

function activeFiltersKeys(activeFilters: any) {
  return activeFilters.map(obj => obj.key).flat()
}

/**
 * Setup behaviour of instantsearch routing.
 * https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/vue/?client=Vue+3#combining-with-nuxtjs
 * @param vueRouter
 * @param activeFilters
 */
export function useNuxtInstantsearchRouter(vueRouter, activeFilters) {
  return {
    /* Reads from the URL and returns a routeState */
    read() {
      return createRouteStateFromRoute(vueRouter.currentRoute, activeFilters)
    },
    /* Writes to the URL */
    write(routeState) {
      // Only push a new entry if the URL changed (avoid duplicated entries in the history)
      if (this.createURL(routeState) === this.createURL(this.read()))
        return

      const route = createRouteFromRouteState(routeState, activeFilters)
      vueRouter.push(route)
    },
    /* Returns a URL as a string */
    createURL(routeState) {
      return createURLFromRouteState(routeState, activeFilters)
    },
    /* Calls this callback whenever the URL changes externally */
    onUpdate(cb) {
      if (typeof window === "undefined")
        return

      this._removeAfterEach = vueRouter.afterEach(() => {
        cb(this.read())
      })

      this._onPopState = () => {
        cb(this.read())
      }
      window.addEventListener("popstate", this._onPopState)
    },
    /* Removes any listeners */
    dispose() {
      if (typeof window === "undefined")
        return

      if (this._onPopState)
        window.removeEventListener("popstate", this._onPopState)

      if (this._removeAfterEach)
        this._removeAfterEach()
    },
  }
}

/**
 * Creates a route object from a typesense state object to be pushed in to Vue router (for history)
 * @param routeState
 * @param activeFilters
 */
function createRouteFromRouteState(routeState, activeFilters) {
  const route = {
    path: "search",
    query: {} as Record<string, any>, // = url params
  }

  // Add main query if it has a value
  sanitizeAndAddIfNotEmpty(route.query, "query", routeState[indexName]?.query)

  // keep potential other "foreign" query params
  // Mirror parseQuery's malformed-URL recovery so foreign queries (UTM, etc.) round-trip.
  const queriesFromUrl = qs.parse(normalizeMalformedQuery(window.location.search.slice(1)))
  const foreignQueries = Object.fromEntries(
    Object.entries(queriesFromUrl).filter(
      ([key]) =>
        !activeFiltersKeys(activeFilters).includes(key) && !(key === "query"),
    ),
  )
  const safeForeignQueries = sanitizeForeignQueries(foreignQueries)
  Object.assign(route.query, safeForeignQueries)

  activeFilters.forEach((filter: any) => {
    let filterObjectKey = FilterTypesInstantSearch.REFINEMENT_LIST
    if (filter.type === FilterTypesContentService.SLIDER)
      filterObjectKey = FilterTypesInstantSearch.RANGE

    const rawValue = routeState[indexName]?.[filterObjectKey]?.[filter.key]
    sanitizeAndAddIfNotEmpty(route.query, filter.key, rawValue)
  })

  return route
}

/**
 * Creates a URL query string from an instantsearch route state
 * @param routeState
 * @param activeFilters
 */
function createURLFromRouteState(routeState, activeFilters) {
  if (routeState) {
    const params: Record<string, any> = {}
    sanitizeAndAddIfNotEmpty(params, "query", routeState[indexName]?.query)

    activeFilters.forEach((filter: any) => {
      let filterObjectKey = FilterTypesInstantSearch.REFINEMENT_LIST
      if (filter.type === FilterTypesContentService.SLIDER)
        filterObjectKey = FilterTypesInstantSearch.RANGE

      const rawValue = routeState[indexName]?.[filterObjectKey]?.[filter.key]
      sanitizeAndAddIfNotEmpty(params, filter.key, rawValue)
    })

    // Mirror parseQuery's malformed-URL recovery so foreign queries (UTM, etc.) round-trip.
    const queriesFromUrl = qs.parse(normalizeMalformedQuery(window.location.search.slice(1)))
    const foreignQueries = Object.fromEntries(
      Object.entries(queriesFromUrl).filter(
        ([key]) =>
          !activeFiltersKeys(activeFilters).includes(key) && !(key === "query"),
      ),
    )
    const safeForeignQueries = sanitizeForeignQueries(foreignQueries)
    return qs.stringify({ ...params, ...safeForeignQueries }, {
      addQueryPrefix: true,
      arrayFormat: "repeat",
      format: "RFC3986",
    })
  }
}

/**
 * Creates a state object for instantsearch from the vue route
 * @param route
 * @param activeFilters
 */
function createRouteStateFromRoute(route, activeFilters) {
  const routeState = {
    [indexName]: {
      query: "", // free text query search
      refinementList: {} as Record<string, string[]>,
      range: {} as Record<string, string>,
    },
  }

  // Sanitize and add the main query
  sanitizeAndAddIfNotEmpty(routeState[indexName], "query", route.value?.query.query)

  activeFilters.forEach((filter: any) => {
    if (filter.type === FilterTypesContentService.SLIDER) {
      sanitizeAndAddIfNotEmpty(routeState[indexName].range, filter.key, route.value?.query[filter.key])
    }
    else {
      const raw = route.value?.query[filter.key]
      if (raw != null) {
        const rawArray = Array.isArray(raw) ? raw : [raw]
        sanitizeAndAddIfNotEmpty(routeState[indexName].refinementList, filter.key, rawArray)
      }
      else {
        // Always make it an array, even when empty
        routeState[indexName].refinementList[filter.key] = []
      }
    }
  })

  return routeState
}
